import { inject, Injectable } from '@angular/core';
import { where } from '@angular/fire/firestore';
import { COLLECTIONS } from '@core/core.constants';
import {
  ChangeRequestField,
  ChangeRequestStatus,
  InscriptionChangeRequest,
  ParticipantRequestableField,
  TeamRequestableField,
  isRequestableField,
} from '@core/models/inscription-change-request.model';
import { Inscription, Participant } from '@core/models/inscription.model';
import { UserService } from '@core/services/user.service';
import { catchError, combineLatest, firstValueFrom, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { EditionService } from '../modules/editions/edition.service';
import { AdminFirestoreService } from './admin-firestore.service';

/** Lo que hace falta para abrir una solicitud; el resto lo pone el servicio. */
export type NewChangeRequest = Pick<
  InscriptionChangeRequest,
  | 'editionId'
  | 'inscriptionId'
  | 'participantSlot'
  | 'participantIndex'
  | 'participantId'
  | 'participantName'
  | 'participantRoleLabel'
  | 'teamName'
  | 'dorsal'
  | 'changes'
  | 'note'
>;

/**
 * Las peticiones de Reservas para corregir datos de participantes: quien lleva las reservas ve las
 * fichas pero no las puede tocar, así que deja aquí lo que hay que cambiar y quien lleva las
 * inscripciones lo aplica.
 */
@Injectable({
  providedIn: 'root',
})
export class InscriptionChangeRequestsService {
  private firestore = inject(AdminFirestoreService);
  private userService = inject(UserService);
  private editionService = inject(EditionService);

  /** Solo Reservas, Inscripciones y administración tienen permiso para leerlas. */
  private canRead$ = combineLatest([
    this.userService.isAdmin$,
    this.userService.isEmployee$,
    this.userService.isBooking$,
  ]).pipe(map(([isAdmin, isEmployee, isBooking]) => isAdmin || isEmployee || isBooking));

  /** Las solicitudes de la edición elegida, de la más reciente a la más antigua. */
  requests$: Observable<InscriptionChangeRequest[]> = combineLatest([
    this.canRead$,
    this.editionService.editionId$,
  ]).pipe(
    switchMap(([canRead, editionId]) => {
      if (!canRead || !editionId) return of<InscriptionChangeRequest[]>([]);
      return this.firestore
        .listDocumentsWithId<InscriptionChangeRequest>(
          COLLECTIONS.INSCRIPTION_CHANGE_REQUESTS,
          where('editionId', '==', editionId),
        )
        .pipe(catchError(() => of<InscriptionChangeRequest[]>([])));
    }),
    map((requests) =>
      [...requests].sort((a, b) => (b.requestedAt?.getTime() ?? 0) - (a.requestedAt?.getTime() ?? 0)),
    ),
    shareReplay(1),
  );

  /** Las que siguen sin resolver, que son las que hay que atender. */
  pendingRequests$: Observable<InscriptionChangeRequest[]> = this.requests$.pipe(
    map((requests) => requests.filter((request) => request.status === 'PENDING')),
    shareReplay(1),
  );

  /** Cuántas quedan por atender, para el aviso del menú. */
  pendingCount$: Observable<number> = this.pendingRequests$.pipe(
    map((requests) => requests.length),
    shareReplay(1),
  );

  /** Las solicitudes de un participante concreto, para verlas sin salir de la tabla de Reservas. */
  requestsForParticipant$(inscriptionId: string, participantId: string | null, participantSlot: string) {
    return this.requests$.pipe(
      map((requests) =>
        requests.filter(
          (request) =>
            request.inscriptionId === inscriptionId &&
            (participantId
              ? request.participantId === participantId
              : request.participantSlot === participantSlot),
        ),
      ),
    );
  }

  async createRequest(request: NewChangeRequest): Promise<void> {
    const user = await firstValueFrom(this.userService.user$);
    await this.firestore.create<InscriptionChangeRequest>(COLLECTIONS.INSCRIPTION_CHANGE_REQUESTS, {
      ...request,
      status: 'PENDING',
      requestedBy: user.id,
      requestedByEmail: user.email ?? '',
      requestedAt: new Date(),
    });
  }

  /**
   * Escribe en la ficha solo los datos pedidos y da la solicitud por hecha.
   *
   * Se escribe campo a campo —'pilot.passport' y no el bloque entero del piloto—, así que ningún
   * otro dato de la inscripción se toca, ni siquiera los del propio participante. La única
   * excepción es la lista de acompañantes: Firestore no sabe cambiar un elemento suelto de una
   * lista, así que ahí hay que reescribirla entera con lo que se acaba de leer.
   */
  async applyRequest(request: InscriptionChangeRequest): Promise<void> {
    const inscription = await firstValueFrom(
      this.firestore.getDocument<Inscription>(COLLECTIONS.INSCRIPTIONS, request.inscriptionId),
    );

    const unknownField = request.changes.find((change) => !isRequestableField(change.scope, change.field));
    if (unknownField) {
      throw new Error(`La solicitud pide cambiar algo que no se puede cambiar desde aquí: ${unknownField.field}.`);
    }

    const fields: Record<string, unknown> = {};
    request.changes
      .filter((change) => change.scope === 'team')
      .forEach((change) => {
        fields[change.field] = change.proposedValue;
      });

    const participantChanges = request.changes.filter((change) => change.scope === 'participant');
    if (participantChanges.length) {
      // Se comprueba que la persona sigue ahí: si no, escribir por su ruta crearía una ficha suelta.
      const participant = this.findParticipant(inscription, request);
      if (!participant) {
        throw new Error('Ya no se encuentra a esta persona en el equipo. Revisa la inscripción antes de aplicarlo.');
      }

      if (request.participantSlot === 'pilot' || request.participantSlot === 'copilot') {
        participantChanges.forEach((change) => {
          fields[`${request.participantSlot}.${change.field}`] = change.proposedValue;
        });
      } else {
        const list = [...(inscription.participants ?? [])];
        const position = list.indexOf(participant);
        if (position < 0) {
          throw new Error('Ya no se encuentra a esta persona en el equipo. Revisa la inscripción antes de aplicarlo.');
        }
        const updated: Participant = { ...participant };
        participantChanges.forEach((change) => {
          updated[change.field as ParticipantRequestableField] = change.proposedValue;
        });
        list[position] = updated;
        fields['participants'] = list;
      }
    }

    await this.firestore.updateFields(COLLECTIONS.INSCRIPTIONS, request.inscriptionId, fields);
    await this.resolve(request, 'APPLIED');
  }

  /** Se rechaza cuando el dato pedido no procede; el motivo se le queda a quien la pidió. */
  rejectRequest(request: InscriptionChangeRequest, reason: string): Promise<void> {
    return this.resolve(request, 'REJECTED', reason);
  }

  /** Quien la pidió puede retirarla mientras nadie la haya atendido. */
  cancelRequest(request: InscriptionChangeRequest): Promise<void> {
    return this.resolve(request, 'CANCELLED');
  }

  /** Lo que pone hoy la ficha, para contrastarlo con lo que se pidió. */
  currentValue(
    inscription: Inscription | undefined,
    request: InscriptionChangeRequest,
    change: ChangeRequestField,
  ): string {
    if (!inscription) return '';
    if (change.scope === 'team') {
      const team = inscription as unknown as Record<TeamRequestableField, string | null>;
      return team[change.field as TeamRequestableField] ?? '';
    }
    const participant = this.findParticipant(inscription, request);
    return participant?.[change.field as ParticipantRequestableField] ?? '';
  }

  private async resolve(
    request: InscriptionChangeRequest,
    status: ChangeRequestStatus,
    resolutionNote?: string,
  ): Promise<void> {
    const user = await firstValueFrom(this.userService.user$);
    await this.firestore.update<InscriptionChangeRequest>(COLLECTIONS.INSCRIPTION_CHANGE_REQUESTS, {
      id: request.id,
      status,
      resolvedBy: user.id,
      resolvedByEmail: user.email ?? '',
      resolvedAt: new Date(),
      resolutionNote: resolutionNote ?? '',
    });
  }

  /**
   * Se busca primero por el identificador de la persona, que no cambia aunque el equipo se
   * reordene; la posición solo se usa con las solicitudes que no lo llevan.
   */
  private findParticipant(inscription: Inscription, request: InscriptionChangeRequest): Participant | null {
    if (request.participantSlot === 'pilot') return inscription.pilot ?? null;
    if (request.participantSlot === 'copilot') return inscription.copilot ?? null;

    const list = inscription.participants ?? [];
    const byId = request.participantId ? list.find((participant) => participant.id === request.participantId) : null;
    if (byId) return byId;

    const index = request.participantIndex;
    return index !== null && index >= 0 && index < list.length ? list[index] : null;
  }
}
